import { HttpClientError } from '@talbenmoshe/atlas-api/client';
import { z } from '@talbenmoshe/atlas-api/client';
import { EmailSignInStartedResponseSchema, EmailSignInVerifiedResponseSchema, JsonWebKeySetSchema, NotSignedInErrorSchema, OpenIdConfigurationSchema, ServiceStatusResponseSchema, SignInRefusedErrorSchema, TooManyCodesErrorSchema } from '../schemas/TripOsIdentityAPISchemas.js';
export class TripOsIdentityAPIClient {
    httpClient;
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /** Report the service name and the skeleton value read from the trip_os_identity database */
    async getTripOsIdentityStatus(request, abortController) {
        const response = await this.httpClient.get('/api/trip-os-identity/v1/status', { abortController });
        const validated = ServiceStatusResponseSchema.parse(response.data);
        return { success: validated };
    }
    /**
     * Email a 6-digit sign-in code to an address
     * Answers identically whether or not the address has an account. A code is valid for 10 minutes and allows 5 attempts; a new code replaces every earlier one. Within 60 seconds of the previous code nothing is sent and the answer is still 202 with the time resend becomes available. At most 5 codes are sent to one address in any 24 hours; past that the answer is 429. The configured smoke address is exempt from both limits.
     */
    async startEmailSignIn(request, abortController) {
        const body = request;
        try {
            const response = await this.httpClient.post('/api/trip-os-identity/v1/auth/email/start', body, { abortController });
            const validated = EmailSignInStartedResponseSchema.parse(response.data);
            return { success: validated };
        }
        catch (error) {
            if (error instanceof HttpClientError) {
                const { status, responseData } = error;
                if (status === 429) {
                    const parsed = TooManyCodesErrorSchema.safeParse(responseData);
                    if (parsed.success) {
                        return { tooManyRequests: parsed.data, error };
                    }
                }
            }
            throw error; // Unknown errors still throw
        }
    }
    /**
     * Check a sign-in code and start a session
     * On success the answer sets the session cookie `__Secure-trip_os_session=<opaque id>; Domain=trip-os.com; Path=/; HttpOnly; Secure; SameSite=Lax; Max-Age=2592000` and carries only the address to load next. The session id is never in the body. On refusal the body says why, how many attempts are left (wrong-code only) and when a new code can be requested.
     */
    async verifyEmailSignIn(request, abortController) {
        const body = request;
        try {
            const response = await this.httpClient.post('/api/trip-os-identity/v1/auth/email/verify', body, { abortController });
            const validated = EmailSignInVerifiedResponseSchema.parse(response.data);
            return { success: validated };
        }
        catch (error) {
            if (error instanceof HttpClientError) {
                const { status, responseData } = error;
                if (status === 401) {
                    const parsed = SignInRefusedErrorSchema.safeParse(responseData);
                    if (parsed.success) {
                        return { unauthorized: parsed.data, error };
                    }
                }
            }
            throw error; // Unknown errors still throw
        }
    }
    /**
     * The discovery document for tokens this service signs
     * Public. The answer carries Cache-Control public, max-age=3600. authorization_endpoint and token_endpoint are listed because OIDC clients require them; this service does not serve them (it only signs short-lived tokens for the router's gate).
     */
    async getOpenIdConfiguration(request, abortController) {
        const response = await this.httpClient.get('/api/trip-os-identity/v1/.well-known/openid-configuration', { abortController });
        const validated = OpenIdConfigurationSchema.parse(response.data);
        return { success: validated };
    }
    /**
     * The public keys that verify tokens this service signs
     * Public. The answer carries Cache-Control public, max-age=3600.
     */
    async getJwks(request, abortController) {
        const response = await this.httpClient.get('/api/trip-os-identity/v1/.well-known/jwks.json', { abortController });
        const validated = JsonWebKeySetSchema.parse(response.data);
        return { success: validated };
    }
    /**
     * Keep the caller's session alive
     * Reads the session cookie. A valid session with less than 29 days left is extended to 30 days from now (at most one write a day per session); every success re-sends the session cookie with Max-Age equal to the session's remaining seconds. No body in either direction on success.
     */
    async renewSession(request, abortController) {
        try {
            await this.httpClient.post('/api/trip-os-identity/v1/session/renew', undefined, { abortController });
            return { success: undefined };
        }
        catch (error) {
            if (error instanceof HttpClientError) {
                const { status, responseData } = error;
                if (status === 401) {
                    const parsed = NotSignedInErrorSchema.safeParse(responseData);
                    if (parsed.success) {
                        return { unauthorized: parsed.data, error };
                    }
                }
            }
            throw error; // Unknown errors still throw
        }
    }
}
//# sourceMappingURL=TripOsIdentityAPIClient.js.map